import type { RidePoint, RideProgressPlan } from "./ride";
import { compileRideProgress } from "./ride";
import type { CameraPlatformId } from "./camera-platform";
import { CAMERA_PLATFORM_VERSION } from "./camera-platform";
import type { CameraDistanceId } from "./camera-distance";
import { resolveCameraDistance } from "./camera-distance";
import { smoothedRouteHeading } from "./route-heading";
import { compileChaseHeading } from "./chase-heading";
import { chaseLookAheadFraction, chaseReferenceZoom } from './chase-framing';
import { cameraFollowAllowance, smoothCameraTarget } from './camera-follow';
import { compileTerrainReveal } from './terrain-reveal';
import { TRACKING_REFERENCE_VIEWPORT, trackingViewportZoomOffset } from './camera-tracking-scale';
import { isCameraAngle } from '../../../renderer/src/camera-angle';
import { nightHawkPass } from './night-hawk';
import { nightHawkDisturbance, resolveNightHawkEffects, type NightHawkEffects } from './night-hawk-effects';
import { NIGHT_HAWK_PLATFORM_PITCH } from './night-hawk-platform';
import { cameraVerticalFieldOfView } from './camera-optics';
import { lensForCameraDistance } from '../../../renderer/src/camera-lens';
import { blackbirdTrackingPose } from './blackbird-camera';

const TILE_SIZE = 512;
const MAX_MERCATOR_LATITUDE = 85.051_129;
const OPENING_SECONDS = 3;
const FINISH_SECONDS = 3;
const MIN_ZOOM = 2;
const REFERENCE_MAX_ZOOM = 16;
const MAX_ZOOM = 18;

/** Map space reserved by the frame layout. */
export interface CameraPadding {
  readonly bottom: number;
  readonly left: number;
  readonly right: number;
  readonly top: number;
}

/** Viewport used to fit geographic content around visible overlays. */
export interface CameraViewport {
  readonly height: number;
  readonly padding: CameraPadding;
  readonly width: number;
}

/** One exact MapLibre camera pose at one film time. */
export interface CameraPose {
  readonly fieldOfView?: number;
  readonly roll?: number;
  readonly bearing: number;
  readonly center: readonly [longitude: number, latitude: number];
  readonly journeyProgress: number;
  readonly phase: "opening" | "traversal" | "finish";
  readonly pitch: number;
  readonly zoom: number;
}

/** Deterministic camera evaluator compiled for one ride and viewport. */
export interface CameraPlan {
  readonly styleId: CameraPlatformId;
  readonly version: 3 | 4;
  evaluate(filmProgress: number): CameraPose;
}

interface ProjectedPoint {
  readonly elevationMeters: number;
  readonly x: number;
  readonly y: number;
}

interface InternalPose {
  readonly fieldOfView?: number;
  readonly roll?: number;
  readonly bearing: number;
  readonly center: ProjectedPoint;
  readonly pitch: number;
  readonly zoom: number;
}

interface PhaseState {
  readonly phase: CameraPose["phase"];
  readonly phaseProgress: number;
  readonly journeyProgress: number;
}

/**
 * Compiles route geometry into a stateless camera evaluator.
 *
 * @param points - Validated route points.
 * @param styleId - Selected motion treatment.
 * @param viewport - Output frame and overlay clearance.
 * @param durationSeconds - Complete film duration.
 * @param cameraDistanceId - Selected tracking distance from the route subject.
 * @returns Camera plan that gives the same pose for every repeated seek.
 * @throws RangeError when route, viewport, or duration cannot be rendered.
 */
export function compileCameraPlan(
  points: readonly RidePoint[],
  styleId: CameraPlatformId,
  viewport: CameraViewport,
  durationSeconds: number,
  cameraDistanceId: CameraDistanceId = "standard",
  cameraAngle?: number,
  nightHawkEffects?: Partial<NightHawkEffects>,
  cameraStyleVersion: 3 | 4 = CAMERA_PLATFORM_VERSION,
): CameraPlan {
  if (cameraAngle !== undefined && !isCameraAngle(cameraAngle)) throw new RangeError('Camera angle must be from 0 through 70 degrees.');
  if (points.length < 2) {
    throw new RangeError("A camera plan requires at least two route points.");
  }
  if (
    !Number.isFinite(durationSeconds)
    || durationSeconds <= 0
    || !Number.isFinite(viewport.width)
    || !Number.isFinite(viewport.height)
    || viewport.width <= viewport.padding.left + viewport.padding.right
    || viewport.height <= viewport.padding.top + viewport.padding.bottom
  ) {
    throw new RangeError("Camera duration and unobstructed viewport must be positive.");
  }

  const maximumZoom = cameraDistanceId === 'telephoto-800' ? 20 : cameraDistanceId === "xx-close" ? 19 : MAX_ZOOM;
  if (cameraStyleVersion === 4 && styleId === 'night-hawk' && !nightHawkEffects) nightHawkEffects = { slew: 0, hunt: 0, tilt: 0, turn: 0, climb: 0, tempo: 1, targetLock: true };
  const sensorEffects = resolveNightHawkEffects(nightHawkEffects);
  const projected = projectRoute(points);
  const routeProgress = compileRideProgress(points);
  const bounds = projectedBounds(projected);
  let stableBearing = normalizeBearing(smoothedRouteHeading(routeProgress, 0));
  let finishBearing = cameraStyleVersion === 3 && styleId === "orbit" ? normalizeBearing(stableBearing + 90) : stableBearing;
  const phases = phaseBoundary(durationSeconds);
  const traversalSeconds = durationSeconds * (phases.finishStart - phases.openingFraction);
  const chaseHeading = styleId === "chase"
    ? compileChaseHeading(
      routeProgress,
      cameraDistanceId,
      traversalSeconds,
    )
    : undefined;
  if (chaseHeading) {
    stableBearing = chaseHeading(0);
    finishBearing = chaseHeading(1);
  }
  const overviewZoom = fitZoom(bounds, viewport, finishBearing);
  const terrainReveal = styleId === 'terrain-reveal' ? compileTerrainReveal(routeProgress, points) : undefined;
  const distanceZoomOffset = resolveCameraDistance(cameraDistanceId).zoomOffset;
  const compactLoop = routeIsCompactLoop(points, projected, bounds);
  const trackingOverviewZoom = fitZoom(bounds, TRACKING_REFERENCE_VIEWPORT, finishBearing);
  const viewportZoomOffset = trackingViewportZoomOffset(viewport);
  const referenceTrackingZoom = compactLoop
    ? clamp(trackingOverviewZoom - 0.45, MIN_ZOOM, REFERENCE_MAX_ZOOM)
    : clamp(trackingOverviewZoom + 2.15, MIN_ZOOM, REFERENCE_MAX_ZOOM);
  const trackingZoom = clamp(
    referenceTrackingZoom + distanceZoomOffset + viewportZoomOffset,
    MIN_ZOOM,
    maximumZoom,
  );
  const finishZoom = compactLoop
    ? clamp(overviewZoom + 0.2, MIN_ZOOM, REFERENCE_MAX_ZOOM)
    : overviewZoom;
  const first = projected[0]!;
  const overviewCenter = boundsCenter(bounds);
  const finish: InternalPose = {
    bearing: finishBearing,
    center: overviewCenter,
    pitch: styleId === "uav" ? 38 : 32,
    zoom: finishZoom,
  };

  const followCenter = (fraction: number, zoom: number, lookAhead = 0): ProjectedPoint => {
    const current = projectedRidePoint(routeProgress, fraction);
    const center = smoothCameraTarget(sample => {
      const point = projectedRidePoint(routeProgress, sample, fraction);
      return lookAhead === 0 ? point : interpolatePoint(point,
        projectedRidePoint(routeProgress, Math.min(1, sample + lookAhead), fraction), 0.68);
    }, fraction, traversalSeconds, cameraFollowAllowance(
      viewport.width - viewport.padding.left - viewport.padding.right,
      viewport.height - viewport.padding.top - viewport.padding.bottom,
      zoom,
    ));
    return { ...current, ...center };
  };

  const trackingPose = (journeyProgress: number, filmProgress: number): InternalPose => {
    if (styleId === 'blackbird') return blackbirdTrackingPose(stableBearing,
      zoom => followCenter(journeyProgress, zoom), distanceZoomOffset, viewportZoomOffset);
    if (styleId === 'night-hawk') {
      const pass = nightHawkEffects ? { bearingOffset: 0, pitch: NIGHT_HAWK_PLATFORM_PITCH } : nightHawkPass(journeyProgress);
      const disturbance = nightHawkDisturbance(filmProgress * durationSeconds, sensorEffects);
      const bearing = normalizeBearing(stableBearing + pass.bearingOffset + disturbance.bearing);
      const zoom = clamp(referenceTrackingZoom + distanceZoomOffset + viewportZoomOffset + disturbance.zoom, MIN_ZOOM, maximumZoom);
      const point = projectedRidePoint(routeProgress, journeyProgress);
      const lagProgress = journeyProgressAtFilm(clamp(filmProgress - disturbance.lagSeconds / durationSeconds, 0, 1), durationSeconds);
      const earlier = projectedRidePoint(routeProgress, lagProgress);
      const dx = earlier.x - point.x, dy = earlier.y - point.y;
      const allowance = .17 * Math.min(viewport.width, viewport.height) / (TILE_SIZE * 2 ** zoom);
      const lagScale = allowance / Math.hypot(allowance, dx, dy);
      const radians = degreesToRadians(bearing);
      const x = disturbance.aimX * viewport.width / (TILE_SIZE * 2 ** zoom);
      const y = disturbance.aimY * viewport.height / (TILE_SIZE * 2 ** zoom);
      return {
        ...(nightHawkEffects ? { roll: disturbance.roll, fieldOfView: disturbance.fieldOfView } : {}),
        bearing,
        center: { ...point, x: point.x + dx * lagScale + Math.cos(radians) * x + Math.sin(radians) * y,
          y: point.y + dy * lagScale - Math.sin(radians) * x + Math.cos(radians) * y },
        pitch: nightHawkEffects ? disturbance.pitch : pass.pitch,
        zoom,
      };
    }
    if (styleId === "uav") {
      return { ...finish, pitch: 38, zoom: overviewZoom };
    }
    if (styleId === "aerial-follow") {
      return {
        bearing: stableBearing,
        center: followCenter(journeyProgress, trackingZoom),
        pitch: 52,
        zoom: trackingZoom,
      };
    }
    if (styleId === "chase") {
      const lookAheadFraction = chaseLookAheadFraction(routeProgress.totalDistanceKilometers, distanceZoomOffset);
      const referenceZoom = chaseReferenceZoom(referenceTrackingZoom);
      const zoom = clamp(referenceZoom + distanceZoomOffset + viewportZoomOffset, MIN_ZOOM, maximumZoom);
      return {
        bearing: chaseHeading!(journeyProgress),
        center: followCenter(journeyProgress, zoom, lookAheadFraction),
        pitch: (cameraDistanceId === "x-close" || cameraDistanceId === "xx-close" || cameraDistanceId === 'telephoto-800') ? 54 : 62,
        zoom,
      };
    }
    if (styleId === "orbit") {
      const referenceZoom = clamp(
        referenceTrackingZoom + 0.2,
        MIN_ZOOM,
        REFERENCE_MAX_ZOOM,
      );
      const zoom = clamp(referenceZoom + distanceZoomOffset + viewportZoomOffset, MIN_ZOOM, maximumZoom);
      return {
        bearing: cameraStyleVersion === 3 ? normalizeBearing(stableBearing - 45 + filmProgress * 90) : stableBearing,
        center: followCenter(journeyProgress, zoom),
        pitch: 55,
        zoom,
      };
    }

    const headingRadians = degreesToRadians(
      smoothedRouteHeading(routeProgress, journeyProgress),
    );
    const reveal = terrainReveal!(journeyProgress);
    const referenceZoom = clamp(
      referenceTrackingZoom + reveal.zoomOffset,
      MIN_ZOOM,
      REFERENCE_MAX_ZOOM,
    );
    const zoom = clamp(referenceZoom + distanceZoomOffset + viewportZoomOffset, MIN_ZOOM, maximumZoom);
    const worldOffset = 78 / (TILE_SIZE * 2 ** zoom);
    const current = followCenter(journeyProgress, zoom);
    const lateral: ProjectedPoint = {
      elevationMeters: current.elevationMeters,
      x: current.x + Math.cos(headingRadians) * worldOffset,
      y: current.y + Math.sin(headingRadians) * worldOffset,
    };
    return {
      bearing: normalizeBearing(stableBearing + reveal.bearingOffset),
      center: lateral,
      pitch: reveal.pitch,
      zoom,
    };
  };

  const trackingStart = trackingPose(0, phaseBoundary(durationSeconds).openingFraction);
  const trackingEnd = trackingPose(1, phaseBoundary(durationSeconds).finishStart);
  const opening = openingPose(
    styleId,
    first,
    overviewCenter,
    overviewZoom,
    trackingZoom,
    stableBearing,
  );

  return {
    styleId,
    version: cameraStyleVersion,
    evaluate(filmProgress) {
      if (!Number.isFinite(filmProgress)) {
        throw new TypeError("Film progress must be finite.");
      }
      const boundedFilmProgress = clamp(filmProgress, 0, 1);
      const phase = phaseAt(boundedFilmProgress, durationSeconds);
      let pose: InternalPose;
      if (styleId === 'night-hawk' || styleId === 'blackbird') {
        pose = trackingPose(phase.journeyProgress, boundedFilmProgress);
      } else if (phase.phase === "opening") {
        pose = interpolatePose(opening, trackingStart, smoothstep(phase.phaseProgress));
      } else if (phase.phase === "finish") {
        pose = interpolatePose(trackingEnd, finish, smoothstep(phase.phaseProgress));
      } else {
        pose = trackingPose(phase.journeyProgress, boundedFilmProgress);
      }
      return {
        ...(pose.roll === undefined ? {} : { roll: pose.roll }),
        fieldOfView: pose.fieldOfView ?? cameraVerticalFieldOfView(styleId === 'uav' ? 50 : lensForCameraDistance(cameraDistanceId), viewport.width, viewport.height),
        bearing: normalizeBearing(pose.bearing),
        center: unproject(pose.center),
        journeyProgress: phase.journeyProgress,
        phase: phase.phase,
        pitch: cameraAngle ?? clamp(pose.pitch, 0, 70),
        zoom: clamp(pose.zoom, MIN_ZOOM, maximumZoom),
      };
    },
  };
}

/**
 * Maps film time to synchronized route, marker, and telemetry progress.
 *
 * @param filmProgress - Normalized complete-film position.
 * @param durationSeconds - Complete film duration.
 * @returns Journey progress held at zero during opening and one during finish.
 */
export function journeyProgressAtFilm(filmProgress: number, durationSeconds: number): number {
  return phaseAt(clamp(filmProgress, 0, 1), durationSeconds).journeyProgress;
}

/** Exact ride-traversal boundaries shared with cinematic waypoint timing. */
export function filmTraversalBounds(durationSeconds: number): { startSeconds: number; endSeconds: number } {
  const phases = phaseBoundary(durationSeconds);
  return { startSeconds: phases.openingFraction * durationSeconds, endSeconds: phases.finishStart * durationSeconds };
}

/**
 * Resolves the named camera phase for visible playback status.
 *
 * @param filmProgress - Normalized complete-film position.
 * @param durationSeconds - Complete film duration.
 * @returns Opening, traversal, or finish phase.
 */
export function cameraPhaseAtFilm(
  filmProgress: number,
  durationSeconds: number,
): CameraPose["phase"] {
  return phaseAt(clamp(filmProgress, 0, 1), durationSeconds).phase;
}

/**
 * Preserves the current camera phase and route position when film length changes.
 *
 * @param filmProgress - Current normalized film position.
 * @param currentDurationSeconds - Duration represented by the current position.
 * @param nextDurationSeconds - Newly selected complete-film duration.
 * @returns Equivalent normalized position in the new timeline.
 */
export function retimeFilmProgress(
  filmProgress: number,
  currentDurationSeconds: number,
  nextDurationSeconds: number,
): number {
  const current = phaseAt(clamp(filmProgress, 0, 1), currentDurationSeconds);
  const next = phaseBoundary(nextDurationSeconds);
  if (current.phase === "opening") {
    return current.phaseProgress * next.openingFraction;
  }
  if (current.phase === "finish") {
    return next.finishStart + current.phaseProgress * (1 - next.finishStart);
  }
  return next.openingFraction
    + current.journeyProgress * (next.finishStart - next.openingFraction);
}

/** Selects an opening composition for each moving treatment. */
function openingPose(
  styleId: CameraPlatformId,
  first: ProjectedPoint,
  overviewCenter: ProjectedPoint,
  overviewZoom: number,
  trackingZoom: number,
  stableBearing: number,
): InternalPose {
  if (styleId === "uav") {
    return { bearing: stableBearing, center: overviewCenter, pitch: 38, zoom: overviewZoom };
  }
  const zoom = clamp(overviewZoom - (styleId === "terrain-reveal" ? 0.45 : 1.15), MIN_ZOOM, MAX_ZOOM);
  const subject = styleId === "terrain-reveal"
    ? interpolatePoint(overviewCenter, first, 0.55)
    : first;
  return {
    bearing: stableBearing,
    center: subject,
    pitch: styleId === "orbit" ? 34 : styleId === "terrain-reveal" ? 44 : 28,
    zoom: Math.min(zoom, trackingZoom - 0.6),
  };
}

/** Resolves the opening, traversal, and finish intervals. */
function phaseAt(filmProgress: number, durationSeconds: number): PhaseState {
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
    throw new RangeError("Film duration must be positive.");
  }
  const { finishStart, openingFraction } = phaseBoundary(durationSeconds);
  if (filmProgress <= openingFraction) {
    return {
      journeyProgress: 0,
      phase: "opening",
      phaseProgress: openingFraction === 0 ? 1 : filmProgress / openingFraction,
    };
  }
  if (filmProgress >= finishStart) {
    return {
      journeyProgress: 1,
      phase: "finish",
      phaseProgress: (filmProgress - finishStart) / (1 - finishStart),
    };
  }
  const journeyProgress = (filmProgress - openingFraction) / (finishStart - openingFraction);
  return { journeyProgress, phase: "traversal", phaseProgress: journeyProgress };
}

/** Keeps at least one second available for traversal in unusually short proofs. */
function phaseBoundary(durationSeconds: number): { finishStart: number; openingFraction: number } {
  const phaseSeconds = Math.min(OPENING_SECONDS, FINISH_SECONDS, Math.max(0, (durationSeconds - 1) / 2));
  const openingFraction = phaseSeconds / durationSeconds;
  return { finishStart: 1 - openingFraction, openingFraction };
}

/** Projects the route into a continuous Web Mercator world, including dateline crossings. */
function projectRoute(points: readonly RidePoint[]): readonly ProjectedPoint[] {
  let previousLongitude = points[0]!.longitude;
  let unwrappedLongitude = previousLongitude;
  return points.map((point, index) => {
    if (index > 0) {
      let delta = point.longitude - previousLongitude;
      if (delta > 180) delta -= 360;
      if (delta < -180) delta += 360;
      unwrappedLongitude += delta;
      previousLongitude = point.longitude;
    }
    return project(unwrappedLongitude, point.latitude, point.elevationMeters);
  });
}

/** Samples the same route position used by the marker and telemetry. */
function projectedRidePoint(route: RideProgressPlan, fraction: number, referenceFraction = fraction): ProjectedPoint {
  const point = route.pointNear(fraction, referenceFraction);
  const reference = route.pointAt(0).longitude;
  let longitude = point.longitude;
  while (longitude - reference > 180) longitude -= 360;
  while (longitude - reference < -180) longitude += 360;
  return project(longitude, point.latitude, point.elevationMeters);
}

/** Detects a small closed route that benefits from a closer finish. */
function routeIsCompactLoop(
  points: readonly RidePoint[],
  projected: readonly ProjectedPoint[],
  bounds: ReturnType<typeof projectedBounds>,
): boolean {
  const diagonal = Math.hypot(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY);
  const closure = Math.hypot(
    projected[0]!.x - projected.at(-1)!.x,
    projected[0]!.y - projected.at(-1)!.y,
  );
  return diagonal < 0.000_15 && closure < diagonal * 0.18 && points.length >= 4;
}

/** Finds the fit zoom for the route inside the unobstructed viewport. */
function fitZoom(
  bounds: ReturnType<typeof projectedBounds>,
  viewport: CameraViewport,
  bearing = 0,
): number {
  const width = Math.max(bounds.maxX - bounds.minX, 1e-9);
  const height = Math.max(bounds.maxY - bounds.minY, 1e-9);
  const bearingRadians = degreesToRadians(bearing);
  const rotatedWidth = width * Math.abs(Math.cos(bearingRadians))
    + height * Math.abs(Math.sin(bearingRadians));
  const rotatedHeight = width * Math.abs(Math.sin(bearingRadians))
    + height * Math.abs(Math.cos(bearingRadians));
  const availableWidth = viewport.width - viewport.padding.left - viewport.padding.right;
  const availableHeight = viewport.height - viewport.padding.top - viewport.padding.bottom;
  return clamp(
    Math.log2(Math.min(
      availableWidth / (rotatedWidth * TILE_SIZE),
      availableHeight / (rotatedHeight * TILE_SIZE),
    )),
    MIN_ZOOM,
    REFERENCE_MAX_ZOOM,
  );
}

/** Produces bounds for projected route points. */
function projectedBounds(points: readonly ProjectedPoint[]) {
  return points.reduce(
    (bounds, point) => ({
      maxX: Math.max(bounds.maxX, point.x),
      maxY: Math.max(bounds.maxY, point.y),
      minX: Math.min(bounds.minX, point.x),
      minY: Math.min(bounds.minY, point.y),
    }),
    { maxX: -Infinity, maxY: -Infinity, minX: Infinity, minY: Infinity },
  );
}

/** Returns the center of projected bounds. */
function boundsCenter(bounds: ReturnType<typeof projectedBounds>): ProjectedPoint {
  return {
    elevationMeters: 0,
    x: (bounds.minX + bounds.maxX) / 2,
    y: (bounds.minY + bounds.maxY) / 2,
  };
}

/** Projects one coordinate into the normalized Web Mercator world. */
function project(longitude: number, latitude: number, elevationMeters: number): ProjectedPoint {
  const boundedLatitude = clamp(latitude, -MAX_MERCATOR_LATITUDE, MAX_MERCATOR_LATITUDE);
  const radians = degreesToRadians(boundedLatitude);
  return {
    elevationMeters,
    x: (longitude + 180) / 360,
    y: (1 - Math.log(Math.tan(radians) + 1 / Math.cos(radians)) / Math.PI) / 2,
  };
}

/** Converts a projected point back to a wrapped geographic coordinate. */
function unproject(point: ProjectedPoint): readonly [number, number] {
  const longitude = ((((point.x * 360 - 180) + 180) % 360) + 360) % 360 - 180;
  const latitude = radiansToDegrees(Math.atan(Math.sinh(Math.PI * (1 - 2 * point.y))));
  return [longitude, latitude];
}

/** Interpolates position and elevation in projected space. */
function interpolatePoint(start: ProjectedPoint, finish: ProjectedPoint, fraction: number): ProjectedPoint {
  return {
    elevationMeters: interpolate(start.elevationMeters, finish.elevationMeters, fraction),
    x: interpolate(start.x, finish.x, fraction),
    y: interpolate(start.y, finish.y, fraction),
  };
}

/** Interpolates camera fields while taking the shortest bearing path. */
function interpolatePose(start: InternalPose, finish: InternalPose, fraction: number): InternalPose {
  const bearingDelta = normalizeBearing(finish.bearing - start.bearing);
  return {
    bearing: start.bearing + bearingDelta * fraction,
    center: interpolatePoint(start.center, finish.center, fraction),
    pitch: interpolate(start.pitch, finish.pitch, fraction),
    zoom: interpolate(start.zoom, finish.zoom, fraction),
  };
}

/** Normalizes an angle to the MapLibre range from -180 through 180. */
function normalizeBearing(degrees: number): number {
  return ((degrees + 180) % 360 + 360) % 360 - 180;
}

/** Cubic easing with zero velocity at both phase boundaries. */
function smoothstep(value: number): number {
  const bounded = clamp(value, 0, 1);
  return bounded * bounded * (3 - 2 * bounded);
}

/** Numeric interpolation helper. */
function interpolate(start: number, finish: number, fraction: number): number {
  return start + (finish - start) * fraction;
}

/** Numeric bounds helper. */
function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

/** Converts degrees to radians. */
function degreesToRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/** Converts radians to degrees. */
function radiansToDegrees(radians: number): number {
  return radians * (180 / Math.PI);
}
